export type IntentionStatus = 'in_progress' | 'completed'

export interface UserProfile {
  id: string
  socketId?: string
  name: string
  role: string
  color: string
  sessions: number
  weekStreak?: number
  cameraOn: boolean
  joinedAt: string
  avatarUrl?: string
  bio?: string
  location?: string
  latitude?: number
  longitude?: number
  company?: string
  profilePublic?: boolean
  showActivity?: boolean
  locationPrecision?: LocationPrecision
  hereFor?: string[]
  createdAt?: string
  activeDays?: number
  citizenship?: string
  basedIn?: string
  socials?: SocialLinks
  /** derived server-side from the private birthday; the date itself never leaves the account */
  age?: number
  /** ISO country for the flag on the globe pin: basedIn, else local GeoIP */
  countryCode?: string
  /** lifetime seconds spent in the room, including the running episode */
  focusSeconds?: number
  media?: PublishedMedia | null
}

export interface SocialLinks {
  x?: string
  instagram?: string
  linkedin?: string
  website?: string
}

export interface PublishedMedia {
  provider: 'mesh' | 'daily'
  sessionId?: string
  trackName?: string
}

export interface ChatMessage {
  id: string
  userId: string
  userName: string
  userColor: string
  body: string
  createdAt: string
  reactions: Record<string, string[]>
}

export interface IntentionStep {
  id: string
  text: string
  completed: boolean
}

export interface Intention {
  id: string
  userId: string
  userName: string
  userRole?: string
  userColor: string
  title: string
  steps: IntentionStep[]
  isPublic: boolean
  status: IntentionStatus
  durationMinutes?: number
  createdAt: string
  completedAt?: string
  reactions: Record<string, string[]>
}

export interface RoomSnapshot {
  users: UserProfile[]
  messages: ChatMessage[]
  intentions: Intention[]
}

/** How much of a person's whereabouts the globe reveals. 'hidden' still
 * shows a pin — parked in Antarctica. */
export type LocationPrecision = 'city' | 'country' | 'hidden'

export interface ClientIdentity {
  id: string
  name: string
  role: string
  color: string
  avatarUrl?: string
  bio?: string
  location?: string
  latitude?: number
  longitude?: number
  company?: string
  profilePublic?: boolean
  showActivity?: boolean
  locationPrecision?: LocationPrecision
  hereFor?: string[]
  createdAt?: string
  citizenship?: string
  basedIn?: string
  birthday?: string
  socials?: SocialLinks
}

/** One appearance on the globe within the replay window: who was where,
 * from when to when. Icons only — no media is ever recorded. */
export interface GlobeReplaySession {
  id: string
  name: string
  color: string
  avatarUrl?: string
  latitude: number
  longitude: number
  location?: string
  joinedAt: number
  leftAt: number | null
}

export interface GlobeReplay {
  since: number
  now: number
  sessions: GlobeReplaySession[]
}

export interface AuthUser extends ClientIdentity {
  email: string
}

export interface VideoSignal {
  target: string
  data: {
    type?: RTCSdpType
    sdp?: string
    candidate?: RTCIceCandidateInit
  }
}

/** FLOWN's exact milestone ladder (sessions attended). */
export const SESSION_BADGES = [
  1, 3, 5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 125, 150,
  175, 200, 225, 250, 275, 300, 325, 350, 375, 400, 450, 500, 550, 600,
  650, 700, 750, 800, 850, 900, 1000, 1250, 1500, 1750,
] as const

/** FLOWN's exact weekly-streak ladder; 52 renders as "1y". */
export const WEEK_BADGES = [1, 2, 3, 5, 10, 20, 30, 40, 50, 52, 60, 70, 80, 90] as const

/** 1000 → "1k", 1250 → "1.25k", like FLOWN's badge faces. */
export function badgeLabel(value: number): string {
  return value >= 1000 ? `${value / 1000}k` : String(value)
}

export function weekBadgeLabel(weeks: number): string {
  return weeks === 52 ? '1y' : `${weeks}w`
}

export function badgeForSessions(sessions: number): number {
  return [...SESSION_BADGES].reverse().find((badge) => sessions >= badge) ?? 0
}

/** Consecutive-week streak from distinct ISO-week start timestamps, newest allowed to be the current or previous week. */
export function weekStreakFromWeeks(weekStarts: number[], now: number): number {
  if (!weekStarts.length) return 0
  const week = 7 * 24 * 60 * 60 * 1000
  const sorted = [...new Set(weekStarts)].sort((a, b) => b - a)
  if (now - sorted[0] >= 2 * week) return 0
  let streak = 1
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index - 1] - sorted[index] === week) streak += 1
    else break
  }
  return streak
}
